import {
  Dialog,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Snackbar,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  TableHead,
  Paper,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Backdrop,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import MuiAlert from "@mui/material/Alert";
import React, { useEffect, useRef, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ja, tr } from "date-fns/locale";
import { ShipmentDialogStyle as styles } from "./ShipmentDialogStyle";
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";
import { addDays, format, format as formatDate } from "date-fns";
import { loginUserSelector } from "../recoil/selectors/loginUserSelector";
import { useApiUpdateAmazonItems } from "../hooks/api/useApiUpdateAmazonItems";
import {
  RequestAmazonItem,
  RequestUpdatePlanRequest,
  ResponseAmazonItem,
  ResponseGetPlanInfoDetailResponse,
  RequestShipmentPackage as ApiShipmentPackage,
  RequestCreateDeliveryOptionsRequest,
  RequestBoxInfo,
  RequestRegisterTrackingDetailRequest,
  RequestGetSettingRequest,
} from "../services/OpenAPI";
import SearchByGenre from "./SearchByGenre";
import BarcodeCanvas from "./BarcodeCanvas";
import { useApiUpdateShipment } from "../hooks/api/useApiUpdateShipment";
import { pendingShipmentUpdatedFlagState } from "../recoil/atoms/pendingShipmentAtoms";
import { exportToCSV } from "../utils/csvExporter";
import { shipmentCSVExportFields } from "../utils/csvExportConfig";
import { Status } from "../constants/status";
import { useApiCreateDeliveryOptions } from "../hooks/api/useApiCreateDeliveryOptions";
import { dateFormat2 } from "../utils/dateFormat2";
import { useApiDownloadDeliveryLabels } from "../hooks/api/useApiDownloadDeliveryLabels";
import { pendingShipmentDetailUpdatedFlagState } from "../recoil/atoms/pendingShipmentDetailAtoms";
import { useApiRegisterTrackingDetail } from "../hooks/api/useApiRegisterTrackingDetail";
import { formatDateTime } from "../utils/formatDateTime";
import { transportationOptionsState } from "../recoil/atoms/transportationOptionsAtoms";
import ConfirmDialog from "./ConfirmDialog";
import { useSetting } from "../hooks/logic/useSetting";
import { useApiGetBaseSetting } from "../hooks/api/useApiGetBaseSetting";
import { isGeneralUser, isNotGeneralUser } from "../constants/roles";
import { lwaNotConnectedMessage } from "../utils/lwaError";
import { toDisplayFnsku } from "../utils/fnsku";

export type SearchGenreOption = {
  id: string;
  label: string;
};

// -----------------------------
// 型定義
// -----------------------------

interface BoxInfo {
  id: string; // 行識別ID
  length: string;
  width: string;
  height: string;
  weight: string;
  count: string;
}

interface SelectedTransportationOption {
  id: number;
  name: string;
  alphaCode: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  statusId: number;
  planInfoDetail: ResponseGetPlanInfoDetailResponse;
}

// -----------------------------
// 固定定義
// -----------------------------

type ResponseAmazonItemKey = keyof ResponseAmazonItem;

type Header = {
  label: string;
  key: ResponseAmazonItemKey;
  width?: string | number;
};

// ヘッダー定義
const headers: Header[] = [
  { label: "画像", key: "imageUrl", width: 80 },
  { label: "商品名", key: "amazonItemName", width: 80 },
  { label: "セット数", key: "setCount", width: 20 },
  { label: "出荷単位数", key: "shippedCount", width: 40 },
  { label: "出荷数", key: "actualShippingCount", width: 30 },
  { label: "賞味期限ラベル", key: "expiryDate", width: 40 },
  { label: "FNSKU", key: "fnskuCode", width: 40 },
  { label: "JANコード", key: "janCodes", width: 40 },
  { label: "SKU", key: "sku", width: 40 },
  // { label: "保管場所", key: "storageLocation", width: 40 },
];

const TABLE_HEADER_FONT_SIZE = "0.65rem";
const TABLE_BODY_FONT_SIZE = "1rem";
const TABLE_ROW_HEIGHT = 110;

const URL_CELLER_CENTRAL = "https://sellercentral.amazon.co.jp/home";
const URL_SEINO_UNYU = "https://net.seino.co.jp/myseino/";

// -----------------------------
// メインコンポーネント
// -----------------------------

const ShipmentDialog: React.FC<Props> = ({
  open,
  planInfoDetail,
  onClose,
  statusId,
}) => {
  const searchGenres: SearchGenreOption[] = [
    { id: "fnsku", label: "FNSKU" },
    { id: "jan", label: "JAN" },
  ];

  useApiUpdateAmazonItems();
  const { updateShipment } = useApiUpdateShipment();
  const {
    createDeliveryOptions,
    isLoading,
    error: deliverError,
  } = useApiCreateDeliveryOptions();
  const { downloadDeliveryLabels } = useApiDownloadDeliveryLabels();
  const { registerTrackingDetail, isLoading: trackcingLoading } =
    useApiRegisterTrackingDetail();
  const { getBaseSetting } = useApiGetBaseSetting();
  const setPendingShipmentUpdateFlag = useSetRecoilState(
    pendingShipmentUpdatedFlagState
  );
  const setPendingShipmentDetailUpdatedFlag = useSetRecoilState(
    pendingShipmentDetailUpdatedFlagState
  );
  const pendingShipmentDetailUpdatedFlag = useRecoilValue(
    pendingShipmentDetailUpdatedFlagState
  );
  const resetPendingShipmentDetailUpdatedFlag = useResetRecoilState(
    pendingShipmentDetailUpdatedFlagState
  );
  const loginUser = useRecoilValue(loginUserSelector);
  const transportationOptions = useRecoilValue(transportationOptionsState);

  const [selectFusku, setSelectFnsku] = useState("");
  const [selectJan, setSelectJan] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedGenreId, setSelectedGenreId] = useState(
    searchGenres[0]?.id ?? ""
  );
  const [seinoCorrectionVal, setSeinoCorrectionVal] = useState<number>(1);

  // -------------------------
  // 状態
  // -------------------------
  const [toast, setToast] = useState<{
    msg: string;
    ok: boolean | null;
  } | null>(null);
  const handleCloseToast = () => setToast(null);

  const [editableAmazonItems, setEditableAmazonItems] = useState<
    ResponseAmazonItem[]
  >([]);

  // 箱情報を複数行で管理
  const [boxes, setBoxes] = useState<BoxInfo[]>([]);

  const [readyToShipDate, setReadyToShipDate] = useState<Date | null>(null);
  // 倉庫到着予定日
  const [arrivalStartDate, setArrivalStartDate] = useState<Date | null>(null);
  const [arrivalEndDate, setArrivalEndDate] = useState<Date | null>(null);
  // 配送方法
  const [selectedTransportationOption, setSelectedTransportationOption] =
    useState<SelectedTransportationOption | null>(null);
  // 伝票番号
  const [preShipNum, setPreShipNum] = useState("");
  const [shipNum, setShipNum] = useState("");
  // FBA納品番号（API使用しない場合のみ）
  const [shipmentConfirmationId, SetShipmentConfirmationId] = useState("");

  const [changeShipNumDialog, setChangeShipNumDialog] = useState(false);
  const [selectedPageType, setSelectedPageType] = useState("PackageLabel_A4_6");

  const PAGE_TYPE_OPTIONS = [
    {
      value: "PackageLabel_A4_4",
      label: "A4 / 4面",
      description:
        "A4サイズ（210×297mm）の用紙に4枚のラベルを2×2で並べます。A4ラベルシール（4面）に印刷して切り離す用途です。",
    },
    {
      value: "PackageLabel_A4_6",
      label: "A4 / 6面",
      description:
        "A4サイズ（210×297mm）の用紙に6枚のラベルを2×3で並べます。A4ラベルシール（6面）向けで、一番枚数を節約できます。",
    },
    {
      value: "PackageLabel_Letter_6",
      label: "Letter / 6面",
      description:
        "USレターサイズ（215.9×279.4mm）の用紙に6枚を2×3で並べます。日本では一般的でない用紙サイズです。",
    },
    {
      value: "PackageLabel_Plain_Paper",
      label: "普通紙",
      description:
        "USレターサイズの普通紙に1枚だけ印刷します（実質1面）。ラベルシールがなくても普通のコピー用紙で印刷できます。",
    },
    {
      value: "PackageLabel_Thermal",
      label: "サーマル",
      description:
        "感熱式ラベルプリンター（DYMO・Zebra等）専用。1枚ずつラベルシールに直接印刷します。専用プリンターが必要です。",
    },
  ];

  // -------------------------
  // 初期データ反映
  // -------------------------
  const preClose = () => {
    setReadyToShipDate(null);
    setArrivalStartDate(null);
    setArrivalEndDate(null);
    setShipNum("");
    onClose();
  };

  const fetchSettingData = async () => {
    const req: RequestGetSettingRequest = {
      tenantId: loginUser?.tenantId,
      baseId: planInfoDetail.baseId,
    };
    const result = await getBaseSetting(req);
    if (result) {
      if (result.isSeinoCorrection) {
        setSeinoCorrectionVal(result.seinoCorrectionValue ?? 1);
      } else {
        setSeinoCorrectionVal(1);
      }
    }
  };

  useEffect(() => {
    if (planInfoDetail?.baseId) {
      // 西濃重量補正値取得
      fetchSettingData();
    }
  }, [planInfoDetail?.baseId]);

  useEffect(() => {
    if (planInfoDetail?.amazonItems) {
      setEditableAmazonItems([...planInfoDetail.amazonItems]);
    }
  }, [planInfoDetail?.amazonItems]);

  useEffect(() => {
    if (!open) return;
    // shipmentBoxes をフォーム用 BoxInfo[] に変換
    const initial = planInfoDetail?.shipmentBoxes?.map((b) => ({
      id: crypto.randomUUID(),
      length: b.boxLength?.toString() ?? "",
      width: b.boxWidth?.toString() ?? "",
      height: b.boxHeight?.toString() ?? "",
      weight: b.boxWeight?.toString() ?? "",
      count: b.boxCount?.toString() ?? "",
      slipNo: b.shipNum ?? "",
    })) ?? [
      {
        id: crypto.randomUUID(),
        length: "",
        width: "",
        height: "",
        weight: "",
        count: "",
        slipNo: "",
      },
    ];
    setBoxes(initial);
  }, [open, planInfoDetail?.shipmentBoxes]);

  useEffect(() => {
    // 発送予定日 初期値
    if (planInfoDetail?.readyToShipDate) {
      setReadyToShipDate(new Date(planInfoDetail.readyToShipDate));
    } else {
      setReadyToShipDate(addDays(new Date(), 1)); // 翌日
    }

    // 倉庫到着予定日 初期値
    if (planInfoDetail?.startDate && planInfoDetail?.endDate) {
      setArrivalStartDate(new Date(planInfoDetail.startDate));
      setArrivalEndDate(new Date(planInfoDetail.endDate));
    } else {
      const base = addDays(new Date(), 1);
      setArrivalStartDate(base);
      setArrivalEndDate(addDays(base, 3));
    }

    if (planInfoDetail?.transportationOptionId) {
      const selected = transportationOptions?.find(
        (opt) => opt.id === planInfoDetail.transportationOptionId
      );
      if (selected) {
        setSelectedTransportationOption({
          id: selected.id ?? 0,
          name: selected.name ?? "",
          alphaCode: selected.alphaCode ?? "",
        });
      }
    }

    // 伝票番号
    if (planInfoDetail?.shipNum) {
      setShipNum(planInfoDetail.shipNum ?? "");
      setPreShipNum(planInfoDetail.shipNum ?? "");
    }
    if (planInfoDetail?.shipmentConfirmationId) {
      SetShipmentConfirmationId(planInfoDetail.shipmentConfirmationId);
    }
  }, [planInfoDetail]);

  // ------------------
  // 日付範囲を20件生成
  // ------------------
  const generateDateRanges = (startDate: Date) => {
    const ranges: { label: string; start: Date; end: Date }[] = [];
    for (let i = 0; i < 20; i++) {
      const from = addDays(startDate, i);
      const to = addDays(from, 3); // 1週間
      ranges.push({
        label: `${format(from, "yyyy-MM-dd")} ～ ${format(to, "yyyy-MM-dd")}`,
        start: from,
        end: to,
      });
    }
    return ranges;
  };

  const arrivalOptions = generateDateRanges(
    planInfoDetail?.startDate
      ? new Date(planInfoDetail.startDate)
      : addDays(new Date(), 1)
  );

  // -------------------------
  // 箱情報ハンドラ
  // -------------------------
  const addBox = () => {
    setBoxes((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        length: "",
        width: "",
        height: "",
        weight: "",
        count: "",
        slipNo: "",
      },
    ]);
  };

  const removeBox = (id: string) => {
    setBoxes((prev) => prev.filter((b) => b.id !== id));
  };

  const updateBoxField = (id: string, key: keyof BoxInfo, val: string) => {
    // 小数点許容のフィールド
    const floatFields: (keyof BoxInfo)[] = [
      "length",
      "width",
      "height",
      "weight",
    ];
    if (floatFields.includes(key)) {
      // 数字または小数（先頭の 0 は許容、空文字も許容）
      if (!/^\d*\.?\d*$/.test(val)) return;
    } else {
      // 整数のみ
      if (!/^\d*$/.test(val)) return;
    }

    setBoxes((prev) =>
      prev.map((b) => {
        if (b.id !== id) return b;

        const updatedBox = { ...b, [key]: val };

        // ★ length, width, height が揃っていれば weight を自動計算
        const length = parseFloat(updatedBox.length || "0");
        const width = parseFloat(updatedBox.width || "0");
        const height = parseFloat(updatedBox.height || "0");

        if (length > 0 && width > 0 && height > 0) {
          const weight = length * width * height * 0.000001 * 280;
          updatedBox.weight = weight.toFixed(2); // 小数2桁に丸め
        } else {
          updatedBox.weight = ""; // 値が揃わないときは空
        }

        return updatedBox;
      })
    );
  };

  // -------------------------
  // 出荷数ハンドラ
  // -------------------------
  const updateShipmentCount = (fnsku: string, v: number) => {
    setEditableAmazonItems((prev) =>
      prev.map((it) =>
        it.fnskuCode === fnsku ? { ...it, actualShippingCount: v } : it
      )
    );
  };

  // -------------------------
  // バリデーション
  // -------------------------
  const isShippingDisabled = React.useMemo(() => {
    // 箱入力必須チェック
    const boxInvalid = boxes.some(
      (b) => !b.length || !b.width || !b.height || !b.count //|| !b.slipNo
    );
    // 実出荷数チェック
    const countMismatch = editableAmazonItems.some(
      (it) =>
        Number(it.shippedCount) / Number(it.setCount ?? 1) !==
        Number(it.actualShippingCount)
    );
    const readeyToShipValid = readyToShipDate === null;
    const arrivalDateValid =
      arrivalStartDate === null || arrivalEndDate === null;
    const transportationOption = selectedTransportationOption === null;
    return (
      boxInvalid ||
      countMismatch ||
      readeyToShipValid ||
      arrivalDateValid ||
      transportationOption
    );
  }, [boxes, editableAmazonItems, selectedTransportationOption]);

  // -------------------------
  // API リクエスト作成
  // -------------------------
  const buildRequest = (
    isShipping: boolean
  ): RequestUpdatePlanRequest | null => {
    if (!loginUser) return null;

    const amazonItems: RequestAmazonItem[] = editableAmazonItems.map((it) => ({
      fnskuCode: it.fnskuCode,
      actualShippingCount: it.actualShippingCount,
    }));

    const packages: ApiShipmentPackage[] = boxes.map((b) => ({
      boxLength: b.length ? Number(b.length) : undefined,
      boxWidth: b.width ? Number(b.width) : undefined,
      boxHeight: b.height ? Number(b.height) : undefined,
      boxWeight: b.weight ? Number(b.weight) : undefined,
      boxCount: b.count ? Number(b.count) : undefined,
    }));

    return {
      tenantId: loginUser.tenantId,
      baseId: loginUser.baseId,
      userId: loginUser.id,
      planId: Number(planInfoDetail.planId),
      isShipping,
      readyToShipDate: readyToShipDate
        ? formatDateTime(readyToShipDate, "09:00:00")
        : "",
      startDate: arrivalStartDate
        ? formatDateTime(arrivalStartDate, "09:00:00")
        : "",
      endDate: arrivalEndDate ? formatDateTime(arrivalEndDate, "09:00:00") : "",
      transportationOptionId: selectedTransportationOption?.id,
      amazonItems,
      packages,
    } as RequestUpdatePlanRequest;
  };

  // -------------------------
  // 二重送信防止
  // 保存・納品処理・発送登録が同時に走ると箱情報の2重登録や二重発送になるため、
  // 実行中は他の操作を受け付けない（state は反映が遅れるので ref で即時判定）
  // -------------------------
  const submittingRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const runExclusive = async (fn: () => Promise<void>) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await fn();
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  // -------------------------
  // API 呼び出し
  // -------------------------
  const execUpdateShipment = (isShipping: boolean) =>
    runExclusive(() => doUpdateShipment(isShipping));

  const doUpdateShipment = async (isShipping: boolean) => {
    const reqBody = buildRequest(isShipping);
    if (!reqBody) return;
    const ok = await updateShipment(reqBody);
    setToast({ msg: ok ? "更新が完了しました" : "更新に失敗しました", ok });
    if (ok && isShipping) setPendingShipmentUpdateFlag(true);
  };

  const handleCreateDeliveryOptions = () =>
    runExclusive(doCreateDeliveryOptions);

  const doCreateDeliveryOptions = async () => {
    const reqBody = buildRequest(false);
    if (!reqBody) return;
    const res = await updateShipment(reqBody);
    if (res) {
      if (!planInfoDetail.planId) return;
      const boxInfos: RequestBoxInfo[] = boxes.map((b) => ({
        contentInformationSource: "BARCODE_2D",
        unitOfMeasurement: "CM",
        weightUnit: "KG",
        height: Number(b.height),
        length: Number(b.length),
        width: Number(b.width),
        weight: Number(b.weight),
        quantity: Number(b.count),
      }));
      const request: RequestCreateDeliveryOptionsRequest = {
        boxes: boxInfos,
        contactEmail: "",
        contactName: planInfoDetail.senderName ?? "",
        contactPhone: planInfoDetail.phoneNumber ?? "",
        readyToShipStart: dateFormat2(readyToShipDate),
        deleveryStartDate: dateFormat2(arrivalStartDate),
        deleveryEndDate: dateFormat2(arrivalEndDate),
        transportationOptionId: selectedTransportationOption?.id,
        planId: planInfoDetail.planId,
        tenantId: loginUser?.tenantId ?? 0,
        userId: planInfoDetail.userId ?? 0,
        isApiPlan: planInfoDetail.isApiPlan ?? true,
        groupNum: planInfoDetail.groupNum ?? 1,
      };
      const { ok, notConnected } = await createDeliveryOptions(request);
      if (ok) {
        setPendingShipmentDetailUpdatedFlag(true);
        setPendingShipmentUpdateFlag(true);
        setToast({ msg: "配送オプションを作成しました。", ok: true });
      } else if (notConnected) {
        setToast({
          msg: lwaNotConnectedMessage(planInfoDetail.userName),
          ok: false,
        });
      }
    }
  };

  const handleRegisterTrackingDetail = () =>
    runExclusive(doRegisterTrackingDetail);

  const doRegisterTrackingDetail = async () => {
    if (!planInfoDetail.planId || shipNum.trim() === "") return;
    const req: RequestRegisterTrackingDetailRequest = {
      planId: planInfoDetail.planId,
      groupNum: planInfoDetail.groupNum ?? 1,
      tenantId: loginUser?.tenantId ?? 0,
      userId: planInfoDetail.userId ?? 0,
      trackingId: shipNum,
      isApiPlan: planInfoDetail.isApiPlan ?? true,
      shipmentConfirmationId: shipmentConfirmationId,
    };
    const { ok, notConnected, notifyFailed } = await registerTrackingDetail(req);
    setToast({
      msg: ok
        ? notifyFailed
          ? "発送処理は完了しましたが、セラー様へのChatwork通知に失敗しました。\nChatworkルームIDの設定・送信アカウントの権限を確認し、別途ご連絡ください。"
          : "発送処理が完了しました。"
        : notConnected
        ? lwaNotConnectedMessage(planInfoDetail.userName)
        : "発送処理に失敗しました。",
      ok: ok && !notifyFailed,
    });
    if (ok) {
      // setPendingShipmentDetailUpdatedFlag(true);
      setPendingShipmentUpdateFlag(true);
      onClose();
    }
  };

  const resetGenreWord = () => {
    setSelectFnsku("");
    setSelectJan("");
    return new Promise<void>((resolve) => {
      setSelectFnsku("");
      setSelectJan("");

      // 状態更新の直後に resolve（1フレーム後に確実に）
      requestAnimationFrame(() => resolve());
    });
  };

  const handleGenreChange = (genreId: string) => {
    setSelectedGenreId(genreId);
    resetGenreWord();
  };

  const handleShipmentCountChange = (
    itemIndex: number,
    newValue: number
  ) => {
    setEditableAmazonItems((prev) =>
      prev.map((item, idx) =>
        idx === itemIndex
          ? { ...item, actualShippingCount: newValue }
          : item
      )
    );
  };

  const handleExportCSV = () => {
    if (!planInfoDetail?.shipmentBoxes?.length) return;

    const enrichedBoxes = planInfoDetail.shipmentBoxes.map((box) => ({
      ...box,
      shippedDate: planInfoDetail.readyToShipDate ?? "",
      userName: planInfoDetail.userName ?? "",
      planName: planInfoDetail.planName ?? "",
      shipNum: planInfoDetail.shipNum ?? "",
      shipmentConfirmationId: planInfoDetail.shipmentConfirmationId,
    }));

    exportToCSV(enrichedBoxes, shipmentCSVExportFields, "plan");
  };

  const downloadLabels = async () => {
    if (!planInfoDetail.planId || !planInfoDetail.groupNum) return;
    const result = await downloadDeliveryLabels(
      planInfoDetail.planId,
      planInfoDetail.groupNum,
      selectedPageType
    );
    if (!result.success) {
      setToast({
        msg: result.notConnected
          ? lwaNotConnectedMessage(planInfoDetail.userName)
          : result.errorMessage ?? "配送ラベルのダウンロードに失敗しました。",
        ok: false,
      });
    }
  };

  const validateStatus = (statusId: number) => {
    return (
      planInfoDetail &&
      planInfoDetail.status !== undefined &&
      planInfoDetail.status !== statusId
    );
  };

  useEffect(() => {
    if (deliverError === null) return;
    if (deliverError.includes("Provided package grouping ids are incorrect")) {
      setToast({
        msg: "箱情報が入力されていないグループがあります。\n全てのグループに入力し「保存」した後に再度実行してください。",
        ok: false,
      });
    }
  }, [deliverError]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
      <ConfirmDialog
        open={changeShipNumDialog}
        title="確認"
        message="伝票番号が変更されています。閉じてもよろしいですか？"
        buttons={[
          {
            label: "いいえ",
            variant: "outlined",
            onClick: () => setChangeShipNumDialog(false),
          },
          {
            label: "はい",
            variant: "contained",
            onClick: () => {
              preClose();
              setChangeShipNumDialog(false);
            },
          },
        ]}
      />

      <Dialog
        open={open}
        onClose={(e, reason) => {
          if (reason !== "backdropClick") onClose();
        }}
        disableEnforceFocus
        maxWidth={false}
        fullWidth={false}
        PaperProps={{
          sx: {
            display: "flex",
            width: "calc(100vw - 40px)",
            height: "calc(100vh - 40px)",
            maxWidth: "none",
            maxHeight: "none",
            overflowY: "auto",
          },
        }}
      >
        {/* ---- ヘッダー ---- */}
        <Box sx={styles.header}>
          <Box sx={{ display: "inline-block", textAlign: "center" }}>
            <Box display="flex" gap={2} justifyContent="center" mb={1}>
              <SearchByGenre
                options={["FNSKU", "JAN"]}
                defaultGenre="FNSKU"
                onGenreChange={handleGenreChange}
                searchKeyword={searchKeyword}
                onKeywordChange={setSearchKeyword}
                autoFocus={true}
              />
            </Box>
            {planInfoDetail && planInfoDetail.shipmentConfirmationId !== "" && (
              <Typography>
                FBA納品番号：{planInfoDetail.shipmentConfirmationId}
              </Typography>
            )}
            <Typography>プラン名：{planInfoDetail?.planName ?? ""}</Typography>
            <Typography>
              ユーザー名：{planInfoDetail?.userName ?? ""}　ショップ名：
              {planInfoDetail?.shopName ?? ""}
            </Typography>
          </Box>
          <Button
            onClick={() => {
              if (preShipNum === shipNum) {
                preClose();
              } else {
                setChangeShipNumDialog(true);
              }
            }}
            sx={styles.closeButton}
          >
            ×
          </Button>
        </Box>
        <Box display="flex" alignItems="center" gap={1} mb={2} px={2}>
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: `${TABLE_ROW_HEIGHT * 3.5}px`,
              overflowY: "auto",
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    {/* <Checkbox
                        checked={
                          checkedIds.size > 0 && checkedIds.size === data.length
                        }
                        indeterminate={
                          checkedIds.size > 0 && checkedIds.size < data.length
                        }
                        onChange={(e) => {
                          e.stopPropagation();
                          handleSelectAll();
                        }}
                      /> */}
                  </TableCell>
                  {/* <TableCell>画像</TableCell> */}
                  {headers.map((header) => (
                    <TableCell
                      key={String(header.key)}
                      sx={{
                        fontSize: TABLE_HEADER_FONT_SIZE,
                        px: 0,
                        width: header.width,
                        textAlign: "center",
                      }}
                      align="center"
                    >
                      <TableSortLabel>{header.label}</TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {editableAmazonItems.map((item, itemIndex) => (
                  <TableRow
                    key={itemIndex}
                    sx={{
                      // backgroundColor:
                      //   item.rowColor === "yellow"
                      //     ? "#fff59d"
                      //     : item.rowColor === "green"
                      //     ? "#81c784"
                      //     : "inherit",
                      fontSize: TABLE_BODY_FONT_SIZE,
                      height: `${TABLE_ROW_HEIGHT}px`,
                    }}
                  >
                    <TableCell
                      padding="checkbox"
                      sx={{
                        textAlign: "center",
                        fontSize: TABLE_BODY_FONT_SIZE,
                      }}
                    >
                      {Number(item.shippedCount) /
                        Number(item.setCount ?? 1) ===
                      Number(item.actualShippingCount) ? (
                        <CheckCircleOutlineIcon color="success" />
                      ) : (
                        <Tooltip
                          title="「出荷単位数」と「出荷数」が異なります"
                          arrow
                        >
                          <ErrorOutlineIcon color="error" />
                        </Tooltip>
                      )}
                    </TableCell>
                    {headers.map((header) => {
                      const value = item[header.key];
                      const align =
                        typeof value === "number" ? "right" : "left";

                      return (
                        <TableCell
                          key={String(header.key)}
                          align={align}
                          sx={{
                            fontSize: TABLE_BODY_FONT_SIZE,
                            width: header.width,
                          }}
                        >
                          {header.key === "imageUrl" ? (
                            <a
                              href={`https://www.amazon.co.jp/dp/${item.asin}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img
                                src={
                                  item.imageUrl ??
                                  "https://via.placeholder.com/200x200?text=No+Image"
                                }
                                alt="no image"
                                style={{
                                  width: 200,
                                  height: 200,
                                  objectFit: "contain",
                                  cursor: "pointer",
                                }}
                              />
                            </a>
                          ) : header.key === "expiryDate" &&
                            typeof value === "string" ? (
                            formatDate(new Date(value), "yyyy/MM/dd")
                          ) : header.key === "fnskuCode" &&
                            typeof value === "string" ? (
                            <BarcodeCanvas value={toDisplayFnsku(value)} />
                          ) : header.key === "actualShippingCount" ? (
                            <TextField
                              size="small"
                              value={value}
                              disabled={
                                isGeneralUser(loginUser?.permissionId) ||
                                validateStatus(1)
                              }
                              onChange={(e) => {
                                if (isGeneralUser(loginUser?.permissionId)) return;
                                const v = e.target.value;
                                if (/^\d*$/.test(v))
                                  handleShipmentCountChange(
                                    itemIndex,
                                    Number(v)
                                  );
                              }}
                              inputProps={{
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                                style: { textAlign: "right" },
                              }}
                              sx={{ width: 100 }}
                            />
                          ) : header.key === "janCodes" &&
                            Array.isArray(value) ? (
                            <Box whiteSpace="pre-line">{value.join("\n")}</Box>
                          ) : header.key === "shippedCount" ? (
                            (item["shippedCount"] ?? 0) /
                            (item["setCount"] ?? 1)
                          ) : (
                            value
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        {/* ★ 箱情報入力エリア ★ */}
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          width="100%"
        >
          <Box display="flex" gap={4} justifyContent="center">
            <Box
              onClick={() => window.open(URL_CELLER_CENTRAL, "_blank")}
              sx={styles.link}
            >
              <Typography>セラーセントラル</Typography>
            </Box>
            <Box
              onClick={() => window.open(URL_SEINO_UNYU, "_blank")}
              sx={styles.link}
            >
              <Typography>西濃運輸リンク</Typography>
            </Box>
          </Box>
        </Box>
        {/* ★ 箱情報 + 操作ボタンを66% / 34% に分割 */}
        <Box
          display="flex"
          gap={2}
          width="100%"
          mt={2}
          sx={{
            borderTop: "1px solid #ccc", // 上部境界線
          }}
        >
          {/* 左側：箱情報と操作ボタン（66%） */}
          <Box
            sx={{
              flex: isNotGeneralUser(loginUser?.permissionId) ? "0 0 60%" : "0 0 100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center", // X方向中央揃え
              gap: 2,
              borderRight: "1px solid #ccc", // 左右の境界線
              padding: 2,
            }}
          >
            <Box
              display="flex"
              gap={2}
              alignItems="center"
              justifyContent="center"
            >
              {/* 発送予定日 */}
              <DatePicker
                label="発送予定日"
                value={readyToShipDate}
                onChange={(newValue) => setReadyToShipDate(newValue)}
                format="yyyy-MM-dd"
                minDate={addDays(new Date(), 1)}
                slotProps={{
                  textField: {
                    size: "small",
                    inputProps: { readOnly: false },
                    InputLabelProps: { sx: { fontSize: "0.9rem" } },
                  },
                }}
                sx={{ width: 180 }}
                disabled={isGeneralUser(loginUser?.permissionId) || validateStatus(1)}
              />

              {/* 倉庫到着予定日 */}
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>倉庫到着予定日</InputLabel>
                <Select
                  value={`${format(
                    arrivalStartDate ?? new Date(),
                    "yyyy-MM-dd"
                  )} ～ ${format(arrivalEndDate ?? new Date(), "yyyy-MM-dd")}`}
                  onChange={(e) => {
                    const selected = arrivalOptions.find(
                      (opt) => opt.label === e.target.value
                    );
                    if (selected) {
                      setArrivalStartDate(selected.start);
                      setArrivalEndDate(selected.end);
                    }
                  }}
                  disabled={isGeneralUser(loginUser?.permissionId) || validateStatus(1)}
                >
                  {arrivalOptions.map((opt) => (
                    <MenuItem key={opt.label} value={opt.label}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* 配送方法 */}
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>配送方法</InputLabel>
                <Select
                  value={selectedTransportationOption?.id ?? ""}
                  onChange={(e) => {
                    const selected = transportationOptions?.find(
                      (opt) => opt.id === Number(e.target.value)
                    );
                    if (selected) {
                      setSelectedTransportationOption({
                        id: selected.id ?? 0,
                        name: selected.name ?? "",
                        alphaCode: selected.alphaCode ?? "",
                      });
                      // 必要に応じてここでsetArrivalEndDate(selected.end)など
                    }
                  }}
                  disabled={isGeneralUser(loginUser?.permissionId) || validateStatus(1)}
                >
                  {transportationOptions
                    ?.slice()
                    .sort(
                      (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
                    )
                    .map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>
                        {opt.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
            {/* 箱情報 */}
            {boxes.map((box, idx) => {
              const length = Number(box.length) || 0;
              const width = Number(box.width) || 0;
              const height = Number(box.height) || 0;
              const weight = Number(box.weight) || 0;

              // 西濃重量（大きい方を採用）
              const seinoWeight =
                length && width && height
                  ? Math.round(
                      length * width * height * 0.000001 * seinoCorrectionVal * 280 * 100
                    ) / 100 // 重量と同じく cm³→m³ 換算し小数2桁
                  : 0;

              return (
                <Box
                  key={box.id}
                  display="flex"
                  gap={1}
                  alignItems="center"
                  flexWrap="wrap"
                  mt={idx === 0 ? 0 : 1}
                >
                  {/* --- 縦・横・高さ・重量・箱数・西濃重量 --- */}
                  {[
                    { label: "縦", key: "length" },
                    { label: "横", key: "width" },
                    { label: "高さ", key: "height" },
                  ].map((f) => (
                    <TextField
                      key={f.key}
                      label={f.label}
                      size="small"
                      value={box[f.key as keyof BoxInfo]}
                      onChange={(e) =>
                        updateBoxField(
                          box.id,
                          f.key as keyof BoxInfo,
                          e.target.value
                        )
                      }
                      sx={{ width: 90 }}
                      disabled={
                        isGeneralUser(loginUser?.permissionId) || validateStatus(1)
                      }
                    />
                  ))}

                  {/* 重量（自動計算, permissionId !== 4 のときのみ表示） */}
                  {isNotGeneralUser(loginUser?.permissionId) && (
                    <TextField
                      label="重量"
                      size="small"
                      value={weight}
                      sx={{ width: 90 }}
                      disabled
                    />
                  )}

                  {/* 箱数 */}
                  <TextField
                    label="箱数"
                    size="small"
                    value={box.count}
                    onChange={(e) =>
                      updateBoxField(box.id, "count", e.target.value)
                    }
                    sx={{ width: 90 }}
                    disabled={
                      isGeneralUser(loginUser?.permissionId) || validateStatus(1)
                    }
                  />

                  {/* 西濃重量（permissionId !== 4 のときのみ表示） */}
                  {isNotGeneralUser(loginUser?.permissionId) && (
                    <TextField
                      label="西濃重量"
                      size="small"
                      value={seinoWeight}
                      sx={{ width: 110 }}
                      disabled
                    />
                  )}

                  {/* 削除ボタン */}
                  {statusId === Status.PendingShipment &&
                    boxes.length > 1 &&
                    isNotGeneralUser(loginUser?.permissionId) && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => removeBox(box.id)}
                        sx={{ minWidth: 32, px: 1 }}
                        disabled={
                          isGeneralUser(loginUser?.permissionId) || validateStatus(1)
                        }
                      >
                        🗑
                      </Button>
                    )}
                </Box>
              );
            })}
            {statusId === Status.PendingShipment &&
              isNotGeneralUser(loginUser?.permissionId) && (
                <Button
                  variant="outlined"
                  onClick={addBox}
                  size="small"
                  sx={{ mt: 1 }}
                  disabled={isGeneralUser(loginUser?.permissionId) || validateStatus(1)}
                >
                  ＋ 追加
                </Button>
              )}

            {/* 操作ボタン（左側に含める） */}
            {isNotGeneralUser(loginUser?.permissionId) && (
              <Box display="flex" gap={2} justifyContent="center" mt={2}>
                {statusId === Status.PendingShipment ? (
                  <>
                    {/* <Button
                      variant="contained"
                      onClick={() => execUpdateShipment(true)}
                      disabled={isShippingDisabled}
                      sx={{ width: 140, height: 48 }}
                    >
                      出荷する
                    </Button> */}
                    <Button
                      variant="contained"
                      onClick={() => execUpdateShipment(false)}
                      sx={{ width: 140, height: 48 }}
                      disabled={
                        submitting ||
                        isGeneralUser(loginUser?.permissionId) ||
                        validateStatus(1)
                      }
                    >
                      保存
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleCreateDeliveryOptions()}
                      disabled={
                        submitting ||
                        isShippingDisabled ||
                        isGeneralUser(loginUser?.permissionId) ||
                        validateStatus(1)
                      }
                      sx={{ width: 140, height: 48 }}
                    >
                      納品処理
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={onClose}
                      sx={{ width: 140, height: 48 }}
                    >
                      OK
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleExportCSV}
                      sx={{ width: 140, height: 48 }}
                    >
                      CSV出力
                    </Button>
                  </>
                )}
              </Box>
            )}
          </Box>

          {/* 右側：34%（今後別UI用に空けておく） */}
          {isNotGeneralUser(loginUser?.permissionId) && (
            <Box
              sx={{
                flex: "0 0 30%",
                display: "flex",
                flexDirection: "column", // 縦方向に並べる
                alignItems: "center", // 中央揃え
                justifyContent: "center",
                padding: 2,
              }}
            >
              {/* ラベル面数セレクト */}
              <FormControl size="small" sx={{ width: 140, mb: 1 }}>
                <InputLabel>ラベル形式</InputLabel>
                <Select
                  value={selectedPageType}
                  label="ラベル形式"
                  onChange={(e) => setSelectedPageType(e.target.value)}
                  renderValue={(value) =>
                    PAGE_TYPE_OPTIONS.find((o) => o.value === value)?.label ??
                    ""
                  }
                  disabled={
                    isGeneralUser(loginUser?.permissionId) ||
                    (validateStatus(2) && validateStatus(3)) ||
                    (planInfoDetail && !planInfoDetail.isApiPlan)
                  }
                >
                  {PAGE_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      <Tooltip title={opt.description} placement="right" arrow>
                        <Box sx={{ width: "100%" }}>{opt.label}</Box>
                      </Tooltip>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* 配送ラベル印刷ボタン */}
              <Button
                variant="contained"
                color="primary"
                onClick={downloadLabels}
                sx={{
                  width: 140,
                  height: 48,
                  mb: planInfoDetail && !planInfoDetail.isApiPlan ? 0.5 : 3,
                }}
                disabled={
                  isGeneralUser(loginUser?.permissionId) ||
                  (validateStatus(2) && validateStatus(3)) ||
                  (planInfoDetail && !planInfoDetail.isApiPlan)
                }
              >
                配送ラベル印刷
              </Button>

              {/* API未使用プランはAmazonからラベルを取得できないため、代替手段まで明示する */}
              {planInfoDetail && !planInfoDetail.isApiPlan && (
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "error.main",
                    textAlign: "center",
                    width: 200,
                    mb: 3,
                  }}
                >
                  API未使用プランのため、配送ラベルはこの画面から印刷できません。
                  <br />
                  <Box
                    component="span"
                    onClick={() => window.open(URL_CELLER_CENTRAL, "_blank")}
                    sx={{ ...styles.link, fontWeight: "bold" }}
                  >
                    セラーセントラル
                  </Box>
                  から印刷してください。
                </Typography>
              )}

              {planInfoDetail && !planInfoDetail.isApiPlan && (
                <TextField
                  label="FBA納品番号"
                  size="small"
                  value={shipmentConfirmationId}
                  onChange={(e) => SetShipmentConfirmationId(e.target.value)}
                  sx={{ width: 250, mb: 1.5 }} // 下に少し余白
                  disabled={
                    isGeneralUser(loginUser?.permissionId) ||
                    !validateStatus(0) ||
                    !validateStatus(1)
                  }
                />
              )}
              {/* 伝票番号入力 */}
              <TextField
                label="伝票番号"
                size="small"
                value={shipNum}
                onChange={(e) => setShipNum(e.target.value)}
                sx={{ width: 250, mb: 1.5 }} // 下に少し余白
                disabled={
                  isGeneralUser(loginUser?.permissionId) ||
                  !validateStatus(0) ||
                  !validateStatus(1)
                }
              />

              {/* 伝票番号登録/納品を完了するボタン */}
              <Button
                variant="contained"
                color="primary"
                sx={{ width: 250, height: 48 }}
                onClick={handleRegisterTrackingDetail}
                disabled={
                  submitting ||
                  isGeneralUser(loginUser?.permissionId) ||
                  !validateStatus(0) ||
                  !validateStatus(1) ||
                  shipNum.trim() === "" ||
                  (!planInfoDetail.isApiPlan && shipmentConfirmationId === "")
                }
              >
                伝票番号登録/納品を完了する
              </Button>
            </Box>
          )}
          <Backdrop
            open={isLoading || trackcingLoading}
            sx={{
              zIndex: (theme) => theme.zIndex.drawer + 1000,
              color: "#fff",
            }}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        </Box>
      </Dialog>
      {/* トースト */}
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={toast?.ok ? 4000 : 12000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={handleCloseToast}
          severity={toast?.ok ? "success" : "error"}
          sx={{ width: "100%", whiteSpace: "pre-line" }}
          elevation={6}
          variant="filled"
        >
          {toast?.msg}
        </MuiAlert>
      </Snackbar>
    </LocalizationProvider>
  );
};

export default ShipmentDialog;
